/**
 * Demo sandbox (docs/features/demo-sandbox/plan.md §4.1).
 *
 * The demo build (.env.demo: REACT_APP_SANDBOX=true) turns every self sign-up into a sandbox agency
 * with sample data. The backend PreSignUp trigger then needs the fictional-data consent and a
 * Turnstile token (Cognito ValidationData), and refuses with a code when sign-ups are paused or at
 * capacity. Cognito wraps the trigger's message: "PreSignUp failed with error DEMO_AT_CAPACITY: ...".
 */
import type { SandboxStatusDto } from '../dtos/demo/sandbox.dto';

export const isSandboxBuild = (): boolean => process.env.REACT_APP_SANDBOX === 'true';

export const SANDBOX_SIGNUP_MESSAGES: Record<string, string> = {
  DEMO_CLOSED: 'New demo sign-ups are paused right now. Please try again later.',
  CONSENT_REQUIRED: 'Please confirm that you will only enter fictional information.',
  CAPTCHA_FAILED: 'The security check failed or expired. Complete it again and retry.',
  DEMO_AT_CAPACITY: 'The demo is at capacity right now. Please try again tomorrow.',
};

/** Friendly text for a sandbox sign-up refusal, or null when the error is something else. */
export function sandboxSignupError(err: unknown): string | null {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  const code = Object.keys(SANDBOX_SIGNUP_MESSAGES).find((key) => message.includes(`${key}:`));
  return code ? SANDBOX_SIGNUP_MESSAGES[code] : null;
}

// ── Sandbox status and the persona switcher (demo-sandbox plan §4.3, §4.4) ──────────────────────

/** The sandbox block of GET /users/me/permissions, or null for a normal tenant / older backend. */
export function normalizeSandbox(value: unknown): SandboxStatusDto | null {
  if (!value || typeof value !== 'object' || (value as { sandbox?: unknown }).sandbox !== true) return null;
  const v = value as Partial<SandboxStatusDto>;
  const seed = v.seed && typeof v.seed === 'object' ? v.seed : { status: 'pending' as const, step: null, errorCode: null };
  return {
    sandbox: true,
    seed: { status: seed.status || 'pending', step: seed.step ?? null, errorCode: seed.errorCode ?? null },
    deleteAt: typeof v.deleteAt === 'string' ? v.deleteAt : null,
    extended: v.extended === true,
    canExtend: v.canExtend === true,
    personas: Array.isArray(v.personas) ? v.personas.filter((p) => p && typeof p.key === 'string') : [],
  };
}

const OWNER_STASH_KEY = 'pelora-sandbox-owner';
const ACTIVE_PERSONA_KEY = 'pelora-sandbox-persona';

export interface ActivePersona { key: string; name: string | null; title: string | null; portal: boolean }

const readJson = <T,>(key: string): T | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

/**
 * Keep the owner's refresh token while a persona is signed in (this tab only, like the session
 * itself), so "Back to my account" returns without a password. Only the first switch stashes.
 */
export function stashOwnerSession(refreshToken: string): void {
  try {
    if (!sessionStorage.getItem(OWNER_STASH_KEY)) sessionStorage.setItem(OWNER_STASH_KEY, JSON.stringify({ refreshToken }));
  } catch {
    // Storage unavailable: "Back to my account" falls back to signing in again
  }
}

export const getOwnerRefreshToken = (): string | null => readJson<{ refreshToken?: string }>(OWNER_STASH_KEY)?.refreshToken || null;

export function setActivePersona(persona: ActivePersona): void {
  try { sessionStorage.setItem(ACTIVE_PERSONA_KEY, JSON.stringify(persona)); } catch { /* ignore */ }
}

export const getActivePersona = (): ActivePersona | null => readJson<ActivePersona>(ACTIVE_PERSONA_KEY);

/** Forget the stash and the active persona (back to the owner, or sign-out). */
export function clearPersonaState(): void {
  try {
    sessionStorage.removeItem(OWNER_STASH_KEY);
    sessionStorage.removeItem(ACTIVE_PERSONA_KEY);
  } catch {
    // Storage unavailable: nothing to clear
  }
}

/** Whole days until an ISO instant (0 when past). Pure. */
export function daysUntil(iso: string | null, now: number = Date.now()): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso) - now;
  return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 86400000)) : null;
}
